<script lang="ts">
  export type { BfSelectOption } from './BfSelectTypes'
</script>

<script setup lang="ts">
  /**
   * Select — 03-components/16-select · OSCloud DS 1.1.34
   */
  import { computed, ref } from 'vue'

  import type { BfSelectOption } from './BfSelectTypes'

  const props = withDefaults(
    defineProps<{
      modelValue?: string | number | null
      options?: BfSelectOption[]
      label?: string
      /** Sentence-case label variant for dense forms. */
      plainLabel?: boolean
      placeholder?: string
      disabled?: boolean
      /** Neutral message under the control. */
      hint?: string
      error?: string
      id?: string
      /** 'md' (default 44px) or 'lg' (52px), both from the form-field foundation. */
      size?: 'md' | 'lg'
      fullWidth?: boolean
      /**
       * Inside a composite (the phone-number field) the host is the form-field:
       * the select drops the mix and its label and message, and keeps a bare slot.
       */
      composed?: boolean
      /** Turns the control into a filtering combobox — for long lists like countries. */
      searchable?: boolean
      /** Placeholder shown in the filter field while the menu is open. */
      searchPlaceholder?: string
    }>(),
    {
      modelValue: null,
      options: () => [],
      label: '',
      plainLabel: false,
      placeholder: 'Select',
      disabled: false,
      hint: '',
      error: '',
      id: undefined,
      size: 'md',
      fullWidth: false,
      composed: false,
      searchable: false,
      searchPlaceholder: 'Search…',
    },
  )

  const emit = defineEmits<{
    'update:modelValue': [value: string | number]
    change: [value: string | number]
  }>()

  // Forward external attrs (aria-describedby, aria-label, …) onto the combobox
  // button rather than the wrapper, so they associate with the control itself.
  defineOptions({ inheritAttrs: false })

  const open = ref(false)
  const activeIndex = ref(-1)
  const query = ref('')

  const selectedOption = computed(() => props.options.find(o => !o.header && o.value === props.modelValue) ?? null)
  const displayLabel = computed(() => selectedOption.value?.display ?? selectedOption.value?.label ?? props.placeholder)
  const hasValue = computed(() => selectedOption.value !== null)

  const listboxId = computed(() => (props.id ? `${props.id}-listbox` : undefined))

  // Only the searchable variant narrows the list; the plain variant always
  // renders every option so its keyboard indices stay stable.
  const visibleOptions = computed(() => {
    const term = query.value.trim().toLowerCase()
    if (!props.searchable || !term) return props.options
    const matches = props.options.filter(o => o.header || `${o.label} ${o.trailing ?? ''}`.toLowerCase().includes(term))
    // A heading names the run under it, so it is dropped once that run is filtered away.
    return matches.filter((o, index) => !o.header || (matches[index + 1] && !matches[index + 1].header))
  })

  /** Headings are read, never picked: navigation and selection step over them. */
  const isSelectable = (option: BfSelectOption) => !option.header

  const nextSelectableIndex = (from: number, step: 1 | -1) => {
    const count = visibleOptions.value.length
    for (let offset = 1; offset <= count; offset++) {
      const index = (from + step * offset + count * offset) % count
      if (isSelectable(visibleOptions.value[index])) return index
    }
    return -1
  }

  const activeOptionId = computed(() => {
    if (!listboxId.value || activeIndex.value < 0) return undefined
    return `${listboxId.value}-option-${activeIndex.value}`
  })

  /** Puts the highlight on the current value, or on the first row when unset. */
  const resetActiveIndex = () => {
    const index = visibleOptions.value.findIndex(o => isSelectable(o) && o.value === props.modelValue)
    activeIndex.value = index >= 0 ? index : visibleOptions.value.findIndex(isSelectable)
  }

  const close = () => {
    open.value = false
    activeIndex.value = -1
    query.value = ''
  }

  const toggle = () => {
    if (props.disabled) return
    open.value = !open.value
    if (open.value) {
      query.value = ''
      resetActiveIndex()
    } else {
      close()
    }
  }

  const select = (option: BfSelectOption) => {
    if (!isSelectable(option)) return
    emit('update:modelValue', option.value)
    emit('change', option.value)
    close()
  }

  const onSearchFocus = () => {
    if (props.disabled || open.value) return
    open.value = true
    query.value = ''
    resetActiveIndex()
  }

  const onSearchInput = (event: Event) => {
    query.value = (event.target as HTMLInputElement).value
    open.value = true
    activeIndex.value = visibleOptions.value.findIndex(isSelectable)
  }

  const onKeydown = (e: KeyboardEvent) => {
    if (props.disabled) return
    const optionCount = visibleOptions.value.length
    switch (e.key) {
      case 'Enter':
        e.preventDefault()
        if (open.value && activeIndex.value >= 0 && optionCount) select(visibleOptions.value[activeIndex.value])
        else if (!open.value) toggle()
        break
      // Space picks a row on the button variant, but has to stay typeable in the filter field.
      case ' ':
        if (props.searchable) break
        e.preventDefault()
        if (open.value && activeIndex.value >= 0 && optionCount) select(visibleOptions.value[activeIndex.value])
        else toggle()
        break
      case 'Escape':
        if (open.value) {
          e.preventDefault()
          close()
        }
        break
      case 'ArrowDown':
        e.preventDefault()
        if (!open.value) toggle()
        else if (optionCount) activeIndex.value = nextSelectableIndex(activeIndex.value, 1)
        break
      case 'ArrowUp':
        e.preventDefault()
        if (open.value && optionCount) activeIndex.value = nextSelectableIndex(activeIndex.value, -1)
        break
    }
  }
</script>

<template>
  <!-- A mix on .form-field: the label, the control slot, the surface, the sizes,
       the focus ring and the message all come from the foundation. Invalid state
       lives on the control's aria-invalid, never on a parallel modifier. -->
  <div
    class="select-field"
    :class="{
      'form-field': !composed,
      'form-field--large': !composed && size === 'lg',
      'form-field--full-width': !composed && fullWidth,
    }"
  >
    <!-- The `label` slot carries a label that is more than a string — a required field's
         `.required-marker`. It replaces the prop's text; the prop stays the plain-string path. -->
    <label
      v-if="(label || $slots.label) && !composed"
      :for="id"
      class="form-field__label"
      :class="{ 'form-field__label--plain': plainLabel }"
    >
      <slot name="label">{{ label }}</slot>
    </label>
    <div v-click-outside="close" class="select-field__slot" :class="{ 'form-field__control': !composed }">
      <input
        v-if="searchable"
        :id="id"
        v-bind="$attrs"
        type="text"
        class="form-field__input select-field__control select-field__control--search"
        :class="{ 'select-field__control--placeholder': !hasValue }"
        role="combobox"
        autocomplete="off"
        aria-autocomplete="list"
        :aria-expanded="open"
        :aria-controls="listboxId"
        :aria-activedescendant="activeOptionId"
        :aria-invalid="error ? 'true' : 'false'"
        :disabled="disabled"
        :value="open ? query : displayLabel"
        :placeholder="open ? searchPlaceholder : placeholder"
        @focus="onSearchFocus"
        @input="onSearchInput"
        @keydown="onKeydown"
      />
      <button
        v-else
        :id="id"
        v-bind="$attrs"
        type="button"
        class="form-field__input select-field__control"
        :class="{ 'select-field__control--placeholder': !hasValue }"
        role="combobox"
        aria-haspopup="listbox"
        :aria-expanded="open"
        :aria-controls="listboxId"
        :aria-activedescendant="activeOptionId"
        :aria-invalid="error ? 'true' : 'false'"
        :disabled="disabled"
        @click="toggle"
        @keydown="onKeydown"
      >
        <span class="select-field__value">{{ displayLabel }}</span>
      </button>
      <span class="select-field__caret" :class="{ 'select-field__caret--open': open }" aria-hidden="true">
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </span>
      <ul v-if="open" :id="listboxId" class="select-field__menu" role="listbox">
        <template v-for="(option, index) in visibleOptions" :key="option.value">
          <li v-if="option.header" class="select-field__option-heading" role="presentation">{{ option.label }}</li>
          <li
            v-else
            :id="listboxId ? `${listboxId}-option-${index}` : undefined"
            class="select-field__option"
            :class="{
              'select-field__option--active': index === activeIndex,
              'select-field__option--selected': option.value === modelValue,
              'select-field__option--blocked': option.blocked,
              'select-field__option--split': option.trailing,
            }"
            role="option"
            :aria-selected="option.value === modelValue"
            @click="select(option)"
            @mouseenter="activeIndex = index"
          >
            <template v-if="option.trailing">
              <span class="select-field__option-label">{{ option.label }}</span>
              <span class="select-field__option-trailing">{{ option.trailing }}</span>
            </template>
            <template v-else>{{ option.label }}</template>
          </li>
        </template>
        <li v-if="!visibleOptions.length" class="select-field__empty">No matches.</li>
      </ul>
    </div>
    <span
      v-if="(error || hint) && !composed"
      class="form-field__message"
      :role="error ? 'alert' : undefined"
      :data-test-id="error ? 'field-error' : undefined"
    >
      {{ error || hint }}
    </span>
  </div>
</template>

<style scoped lang="scss">
  /* The foundation, for the elements this component renders. Identical to
     BfInput's so a select next to an input in a row reads as the same control. */
  .form-field {
    display: flex;
    flex-direction: column;
    min-width: 0;

    &--full-width {
      width: 100%;
    }

    &__label {
      font-family: var(--bf-font-display);
      font-size: 11px;
      font-weight: var(--bf-fw-bold);
      color: var(--bf-off-black);
      text-transform: uppercase;
      letter-spacing: var(--bf-tr-eyebrow);
    }

    > .form-field__label {
      margin-bottom: var(--bf-form-label-gap);
    }

    &__label--plain {
      font-size: var(--bf-fs-xs);
      font-weight: var(--bf-fw-semibold);
      line-height: var(--bf-lh-normal);
      color: var(--bf-fg-1);
      text-transform: none;
      letter-spacing: var(--bf-tr-normal);
    }

    &__control {
      position: relative;
    }

    &__input {
      width: 100%;
      font-family: var(--bf-font-body);
      font-size: 15px;
      height: var(--bf-form-control-height);
      padding: 0 14px;
      color: var(--bf-fg-1);
      background: var(--bf-form-control-surface);
      border: 1px solid var(--bf-form-control-hairline);
      border-radius: var(--bf-form-control-radius);
      transition:
        border-color var(--bf-dur-fast) var(--bf-ease),
        box-shadow var(--bf-dur-fast) var(--bf-ease),
        background var(--bf-dur-fast) var(--bf-ease);

      &::placeholder {
        color: var(--bf-form-control-placeholder);
      }

      &:hover:not(:focus):not(:disabled) {
        border-color: var(--bf-form-control-hairline-hover);
      }

      /* aria-expanded="true" is the open combobox, which keeps its ring while
         the pointer is in the list. */
      &:focus-visible,
      &[aria-expanded='true'] {
        outline: none;
        border-color: var(--bf-form-control-hairline-focus);
        box-shadow: var(--bf-form-focus-ring);
        background: var(--bf-form-control-surface-focus);
      }

      &:disabled {
        opacity: var(--bf-form-disabled-opacity);
        cursor: not-allowed;
      }

      &[aria-invalid='true'] {
        border-color: var(--bf-form-invalid-hairline);
        background: var(--bf-form-invalid-surface);
      }
    }

    &--large &__input {
      height: var(--bf-form-control-height-large);
      font-size: var(--bf-fs-base);
    }

    &__message {
      color: var(--bf-fg-2);
      /* Stated, not inherited: the app's body element still carries the legacy family, so a
         message that leaves this open reads in a different face from the control above it
         and from BfUploadField's own constraint line. */
      font-family: var(--bf-font-body);
      font-size: var(--bf-fs-xs);
      display: flex;
      align-items: center;
      gap: 6px;
      margin-top: var(--bf-form-message-gap);
    }

    &__message[role='alert'],
    &:has([aria-invalid='true']) &__message {
      color: var(--bf-form-invalid-text);
    }
  }

  /* The select's own contribution: the slot anchor, the control's row layout,
     the caret and the listbox. Height and surface come from the mix above. */
  .select-field {
    display: flex;
    flex-direction: column;

    /* The slot is .form-field__control when the select is the field; inside a
       composite (phone number) the select keeps its own relative anchor. */
    &__slot {
      position: relative;
    }

    /* Right pad clears the caret, which sits over the control rather than in it
       so the searchable variant can be a bare <input>. */
    &__control {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--bf-sp-2);
      padding: 0 44px 0 16px;
      text-align: left;
      cursor: pointer;

      &--search {
        cursor: text;
      }
    }

    /* The search input shows the picked label as its value; when nothing is
       picked it shows the placeholder text as a value, in placeholder colour. */
    &__control--search.select-field__control--placeholder,
    &__control--placeholder &__value {
      color: var(--bf-form-control-placeholder);
    }

    &__value {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &__caret {
      position: absolute;
      top: 50%;
      right: 14px;
      display: inline-flex;
      color: var(--bf-dusky-1);
      pointer-events: none;
      transform: translateY(-50%);
      transition: transform var(--bf-dur-base) var(--bf-ease);

      &--open {
        transform: translateY(-50%) rotate(180deg);
      }
    }

    &__menu {
      position: absolute;
      top: calc(100% + 4px);
      right: 0;
      left: 0;
      z-index: 50;
      /* Component-local: a host (the phone-number field's 160px code column) may
         set this so the menu grows past a narrow control. */
      min-width: var(--select-field-menu-min-width, 0);
      max-height: 240px;
      margin: 0;
      padding: 4px;
      overflow-y: auto;
      list-style: none;
      background: var(--bf-white);
      border: 1px solid var(--bf-line);
      border-radius: var(--bf-r-lg);
      box-shadow: var(--bf-shadow-2);
    }

    &__option {
      padding: 10px 12px;
      font-family: var(--bf-font-body);
      font-size: 15px;
      color: var(--bf-fg-1);
      border-radius: var(--bf-r-md);
      cursor: pointer;

      /* A secondary label on the right of the row. The row goes flex; the label
         keeps the left. */
      &--split {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--bf-sp-2);
      }

      &--active {
        background: var(--bf-bg-soft);
      }

      &--blocked {
        color: var(--bf-restricted-accent);
      }

      &--selected {
        color: var(--bf-orange-dark);
        font-weight: var(--bf-fw-semibold);
      }
    }

    /* Read, never picked: the eyebrow recipe, so a heading cannot be mistaken for a row.
       Tertiary ink, as the design's own port of Select sets on it — it names the run rather
       than being read as one, and the rows under it carry the contrast. */
    &__option-heading {
      padding: 10px 12px 4px;
      font-family: var(--bf-font-display);
      font-size: 11px;
      font-weight: var(--bf-fw-bold);
      color: var(--bf-fg-3);
      text-transform: uppercase;
      letter-spacing: var(--bf-tr-eyebrow);
    }

    &__option-trailing {
      flex-shrink: 0;
      white-space: nowrap;
      color: var(--bf-fg-2);
    }

    /* Kit extension: the selected row reads as one — the trailing label (a phone code) takes
       the row's orange instead of staying secondary, as the DS card leaves it. */
    &__option--selected &__option-trailing {
      color: inherit;
    }

    &__empty {
      padding: 10px 12px;
      font-family: var(--bf-font-body);
      font-size: var(--bf-fs-sm);
      color: var(--bf-fg-2);
    }
  }
</style>
